import React, { useEffect, useState } from 'react';
import { useMembership } from '@/contexts/MembershipContext';
import { Crown, Sparkles, Pencil, Check, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const greetingForHour = (h: number) => {
  if (h < 5) return 'Good night';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 21) return 'Good evening';
  return 'Good night';
};

const WelcomeBanner: React.FC = () => {
  const { isMember, familyName, memberName, memberEmail, setFamilyName } = useMembership();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [dismissed, setDismissed] = useState(false);

  // Auto-prompt legacy members who don't yet have a family name set
  useEffect(() => {
    if (isMember && !familyName) {
      setEditing(true);
    }
  }, [isMember, familyName]);

  if (!isMember) return null;

  const greeting = greetingForHour(new Date().getHours());
  const firstName = (memberName || '').trim().split(' ')[0] || (memberEmail ? memberEmail.split('@')[0] : '');
  const circleTitle = familyName ? `The ${familyName} Circle Of Kins` : null;

  const handleSave = () => {
    const clean = draft.trim();
    if (!clean) return;
    setFamilyName(clean);
    setEditing(false);
    setDraft('');
  };

  // Editing / first-time prompt view
  if (editing) {
    return (
      <section className="bg-gradient-to-r from-amber-900 via-amber-800 to-green-900 text-amber-50">
        <div className="max-w-6xl mx-auto px-4 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-full bg-amber-50/10 border border-amber-300/30 flex items-center justify-center flex-shrink-0">
              <Crown className="w-5 h-5 text-amber-200" />
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-amber-200/80">
                {familyName ? 'Edit your circle name' : 'Name your family circle'}
              </div>
              <div className="font-serif text-lg sm:text-xl font-bold truncate">
                What is your family's last name?
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape' && familyName) setEditing(false);
              }}
              placeholder="e.g. Walker"
              className="bg-white/95 text-amber-950 placeholder:text-amber-700/50 border-amber-300 w-full sm:w-48"
            />
            <Button
              onClick={handleSave}
              disabled={!draft.trim()}
              className="bg-amber-50 text-amber-900 hover:bg-white"
            >
              <Check className="w-4 h-4 mr-1" /> Save
            </Button>
            {familyName && (
              <Button
                variant="ghost"
                onClick={() => { setEditing(false); setDraft(''); }}
                className="text-amber-100 hover:bg-amber-50/10 hover:text-white"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        {draft.trim() && (
          <div className="max-w-6xl mx-auto px-4 pb-3 -mt-1">
            <span className="text-xs text-amber-200/80">
              You'll be greeted as{' '}
              <span className="italic font-medium text-amber-100">
                "The {draft.trim()} Circle Of Kins"
              </span>
            </span>
          </div>
        )}
      </section>
    );
  }

  if (dismissed || !circleTitle) return null;

  return (
    <section className="bg-gradient-to-r from-amber-900 via-amber-800 to-green-900 text-amber-50">
      <div className="max-w-6xl mx-auto px-4 py-5 flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-amber-50/10 border border-amber-300/30 flex items-center justify-center flex-shrink-0">
          <Crown className="w-6 h-6 text-amber-200" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-amber-200/80 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            {greeting}{firstName ? `, ${firstName}` : ''}
          </div>
          <div className="font-serif text-xl sm:text-2xl font-bold truncate">
            Welcome to {circleTitle}
          </div>
          <div className="text-xs text-amber-200/70 mt-0.5 hidden sm:block">
            Your family network — every generation, every household, all in one private space.
          </div>

        </div>
        <button
          onClick={() => { setDraft(familyName || ''); setEditing(true); }}
          className="text-xs text-amber-100 hover:text-white flex items-center gap-1 px-2 py-1 rounded-md hover:bg-amber-50/10"
          aria-label="Edit family name"
          title="Edit family name"
        >
          <Pencil className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Edit</span>
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="text-amber-100/70 hover:text-white p-1"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
};

export default WelcomeBanner;
